import style from "@/styles/pages/PropertyDetails/PropertyDetails.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import ManualAddress from "@/components/Address/ManualAddress";
import { useDispatch, useSelector } from "react-redux";
import {
  setPAStreetNumber,
  setPAStreetName,
  setPAUnitNumber,
  setPACity,
  setPAProvince,
  setPAPostalCode,
  setPACountry,
  selectPropertyAddress,
  setPAStreetType,
  setPAStreetDirection,
  setPAUnitType,
} from "@/slices/propertyAddressSlice";
import { setProfileComplete } from "@/slices/progressSlice";
import { useNavigate } from "react-router-dom";
import { selectApplication, setApplicationProgress } from "@/slices/applicationSlice";
import CurrencyInput from "react-currency-input-field";
import { selectPropertyInfo, setHeatingCost, setHeatingType, setLivableArea, setLivableAreaUnit, setMaintenanceFee, setMortgageBalance, setMortgageHolder, setOriginalPurchasePrice, setPropertyTax, setPropertyValue, setPurchasePrice } from "@/slices/propertyInfoSlice";
import { RootState } from "@/store/dtm";
import { selectCoApplicant } from "@/slices/coApplicantSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { addressType } from "@/types/addressType";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { AnimatePresence, motion } from "framer-motion";
import { createApiClient } from "@/lib/Requests";
import { AddressDto, ApplicationDto, PropertyForCreationDto, SubjectPropertyDto, TokenData } from "@/lib/DTMWebAPIClient.generated";
import { applicationCategory } from "@/types/applicationType";
import dayjs from "dayjs";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "@/context/AuthContext";
import { selectPreviousEmployment } from "@/slices/previousEmploymentSlice";
import { selectCurrentEmployment } from "@/slices/currentEmploymentSlice";
import { setCachedAssets } from "@/slices/sessionDataSlice";
import LegalToolTip from "@/components/LegalTerms/LegalToolTip";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function PropertyDetails() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [manualAddressShown, setmanualAddressShown] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [addressSelectionError, setAddressSelectionError] = useState(false);
  const [price, setPrice] = useState("");
  const [propertyValueAmount, setPropertyValueAmount] = useState("");
  const [originalPurchasePriceAmount, setOriginalPurchasePriceAmount] = useState("");
  const [remainingBalanceAmount, setRemainingBalanceAmount] = useState("");
  const [propertyTaxAmount, setPropertyTaxAmount] = useState("");
  const [heatingCostAmount, setHeatingCostAmount] = useState("");
  const [maintenanceAmount, setMaintenanceAmount] = useState("");
  const [displayError, setDisplayError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [currentAddressInfo, setCurrentAddressInfo] = useState<addressType>({
    id: 0,
    streetNumber: "",
    streetName: "",
    streetType: "",
    streetDirection: "",
    city: "",
    province: "",
    country: "",
    postalCode: "",
    unitType: "",
    unitNumber: ""
  });


  const dispatch = useDispatch();

  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const propertyAddress = useSelector(selectPropertyAddress);
  const application = useSelector(selectApplication).application;
  const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
  const coapplicants = useSelector(selectCoApplicant).coapplicants;
  const assets = useSelector((state: RootState) => state.assets.assets);
  const previousEmployment = useSelector(selectPreviousEmployment);
  const credential = useSelector(
    (state: RootState) => state.credential
  );
  const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
  const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

  const methods = useForm({
    defaultValues: {
      autoAddress: "",
      streetNumber: propertyAddress.address.streetName,
      streetName: propertyAddress.address.streetNumber,
      streetType: propertyAddress.address.streetType,
      streetDirection: propertyAddress.address.streetDirection,
      unitNumber: propertyAddress.address.unitNumber,
      unitType: propertyAddress.address.unitType,
      cityName: propertyAddress.address.city,
      provinceName: propertyAddress.address.province,
      postalCode: propertyAddress.address.postalCode,
      country: propertyAddress.address.country,
      purchasePrice: "",
      propertyValue: "",
      originalPurchasePrice: "",
      remainingBalance: "",
      mortgageHolder: "",
      propertyTax: "",
      livableArea: "",
      livableAreaUnit: "",
      heatingCost: "",
      heatingType: "",
      maintenanceFee: "",
    },
  });

  const fields = [
    {
      element: "search",
      field: "",
      mode: window.pca?.fieldMode.SEARCH,
    },

    {
      element: "span-street-address",
      field: "Line1",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-street-address2",
      field: "Line2",
      mode: window.pca?.fieldMode?.POPULATE,
    },
    { element: "city", field: "City", mode: window.pca?.fieldMode.POPULATE },
    {
      element: "span-state",
      field: "ProvinceCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-postcode",
      field: "PostalCode",
      mode: window.pca?.fieldMode.POPULATE,
    },
    {
      element: "span-country",
      field: "CountryName",
      mode: window.pca?.fieldMode.POPULATE,
    },
  ];
  const options = {
    key: "XD95-YY77-BP99-EM55",
  };

  const toggleManualAddress = () => {
    setmanualAddressShown(!manualAddressShown);
    if (!manualAddressShown) {
      formatAddress();
      setShowAddress(false);
    } else {
      setShowAddress(addressSelected);
    }
  };

  useEffect(() => {
    document.title = "Tell us about your property";

    dispatch(setProfileComplete(true));
    dispatch(setCachedAssets(assets));
    setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

    methods.setValue("purchasePrice", propertyInfo ? propertyInfo.purchasePrice == 0 ? "" : propertyInfo.purchasePrice.toString() : "");
    setPrice(propertyInfo ? propertyInfo.purchasePrice == 0 ? "" : propertyInfo.purchasePrice.toString() : "");
    methods.setValue("propertyValue", propertyInfo ? propertyInfo.propertyValue == 0 ? "" : propertyInfo.propertyValue.toString() : "");
    setPropertyValueAmount(propertyInfo ? propertyInfo.propertyValue == 0 ? "" : propertyInfo.propertyValue.toString() : "");
    methods.setValue("originalPurchasePrice", propertyInfo ? propertyInfo.originalPurchasePrice == 0 ? "" : propertyInfo.originalPurchasePrice.toString() : "");
    setOriginalPurchasePriceAmount(propertyInfo ? propertyInfo.originalPurchasePrice == 0 ? "" : propertyInfo.originalPurchasePrice.toString() : "");
    methods.setValue("remainingBalance", propertyInfo ? propertyInfo.mortgageBalance == 0 ? "" : propertyInfo.mortgageBalance.toString() : "");
    setRemainingBalanceAmount(propertyInfo ? propertyInfo.mortgageBalance == 0 ? "" : propertyInfo.mortgageBalance.toString() : "");
    methods.setValue("mortgageHolder", propertyInfo ? propertyInfo.mortgageHolder : "");
    setMortgageHolder(propertyInfo ? propertyInfo.mortgageHolder : "");
    methods.setValue("propertyTax", propertyInfo ? propertyInfo.propertyTax == 0 ? "" : propertyInfo.propertyTax.toString() : "");
    setPropertyTaxAmount(propertyInfo ? propertyInfo.propertyTax == 0 ? "" : propertyInfo.propertyTax.toString() : "");
    methods.setValue("livableArea", propertyInfo ? propertyInfo.livableArea.toString() : "");
    methods.setValue("livableAreaUnit", propertyInfo.livableAreaUnit);
    methods.setValue("heatingCost", propertyInfo ? propertyInfo.heatingCost == 0 ? "" : propertyInfo.heatingCost.toString() : "");
    setHeatingCostAmount(propertyInfo ? propertyInfo.heatingCost == 0 ? "" : propertyInfo.heatingCost.toString() : "");
    methods.setValue("heatingType", propertyInfo ? propertyInfo.heatingType : "");
    setMaintenanceAmount(propertyInfo ? propertyInfo.maintenanceFee == 0 ? "" : propertyInfo.maintenanceFee.toString() : "");

    const control = new window.pca.Address(fields, options);
    control.listen("populate", (address: any) => {
      let streetInfo = address.Line1.split(" ");
      let streetName = "";
      let streetDirection = "";
      let streetType = "";
      let streetComponents: string[] = address.Street.split(" ");
      let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

      //Check if street direction and type exists in the street name
      if (streetDirections.includes(lastStreetComponent)) {
        streetDirection = lastStreetComponent;
        let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
        if (streetTypes.includes(secondLastStreetComponent)) {
          streetType = secondLastStreetComponent;
        }
        streetComponents.forEach((value, index) => {
          if (index < streetComponents.length - 2) {
            streetName += value + " "
          }
        });
      } else if (streetTypes.includes(lastStreetComponent)) {
        streetType = lastStreetComponent;
        streetComponents.forEach((value, index) => {
          if (index != streetComponents.length - 1) {
            streetName += value + " "
          }
        });
      } else {
        streetComponents.forEach((value, index) => {
          streetName += value + " "
        });
      }
      streetName = streetName.trim();

      //Check if unit number is embeded in the street number
      let streetNumber = "";
      let streetNumberComponents = streetInfo[0].split("-");
      if (streetNumberComponents.length > 1) {
        streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
      } else {
        streetNumber = streetInfo[0];
      }

      let addressInfo: addressType = {
        id: 0,
        streetNumber: streetNumber,
        streetName: streetName,
        streetType: streetType,
        streetDirection: streetDirection,
        city: address.City,
        province: address.ProvinceCode,
        country: address.CountryName,
        postalCode: address.PostalCode,
        unitType: address.SubBuilding != undefined && address.SubBuilding != "" ? "unit" : "",
        unitNumber: address.SubBuilding != undefined ? address.SubBuilding : "",
      }
      setCurrentAddressInfo(addressInfo);

      if (address.SubBuilding != undefined && address.SubBuilding != "") {
        methods.setValue("unitNumber", addressInfo.unitNumber);
        methods.setValue("unitType", "unit");
        methods.register("unitType", {
          required: "Select a unit type",
          pattern: {
            value: /^[ #a-zA-Z0-9._-]+$/,
            message: "Select a unit type",
          },
          validate: {
            unitType: (c) => {
              if (c.trim() != "") {
                return true
              } else {
                return "Select a unit type";
              }
            },
          },
        });
      }

      methods.setValue("streetNumber", addressInfo.streetNumber);
      methods.setValue("streetName", addressInfo.streetName);
      methods.setValue("streetType", addressInfo.streetType);
      methods.setValue("streetDirection", addressInfo.streetDirection);
      methods.setValue("cityName", addressInfo.city);
      methods.setValue("provinceName", addressInfo.province);
      methods.setValue("postalCode", addressInfo.postalCode);
      methods.setValue("country", addressInfo.country);
      methods.clearErrors();

      if (address.ProvinceName?.trim().toLowerCase() != "ontario") {
        setDisplayError(true);
      } else {
        setDisplayError(false);
      }
    });

    //Refresh Token if it is expired
    if (credential.credential.expiration && dayjs(credential.credential.expiration).diff(dayjs(), 'minute') < 1) {
      client.apiAuthRefreshtoken(new TokenData({
        accessToken: credential.credential.accessToken,
        refreshToken: credential.credential.refreshToken
      })).then((result) => {
        login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");
        updateAppData();
      })
    } else {
      updateAppData();
    }

    if (propertyAddress != undefined && propertyAddress.address != undefined && propertyAddress.address.city != "") {
      try {
        const span = document.getElementById("span-street-address");
        if (span != null) {
          span.innerText = (propertyAddress.address.unitNumber != "" ? propertyAddress.address.unitNumber + "-" : "") +
            propertyAddress.address.streetNumber + " " + propertyAddress.address.streetName + " "
            + propertyAddress.address.streetType + (propertyAddress.address.streetDirection.trim() != "" ? (" " + propertyAddress.address.streetDirection) : "");
          methods.setValue("autoAddress", span.innerText);
        }
        const span2 = document.getElementById("span-city");
        if (span2 != null) {
          span2.innerText = propertyAddress.address.city;

        }
        const span3 = document.getElementById("span-state");
        if (span3 != null) {
          span3.innerText = propertyAddress.address.province;

        }
        const span4 = document.getElementById("span-postcode");
        if (span4 != null) {
          span4.innerText = propertyAddress.address.postalCode;

        }
        const span5 = document.getElementById("span-country");
        if (span5 != null) {
          span5.innerText = propertyAddress.address.country;

        }
      } catch (e) { }
      setShowAddress(true);
      setAddressSelected(true);
      setAddressSelectionError(false);

      let addressInfo: addressType = {
        id: propertyAddress.address.id,
        streetNumber: propertyAddress.address.streetNumber,
        streetName: propertyAddress.address.streetName,
        streetType: propertyAddress.address.streetType,
        streetDirection: propertyAddress.address.streetDirection,
        city: propertyAddress.address.city,
        province: propertyAddress.address.province,
        country: propertyAddress.address.country,
        postalCode: propertyAddress.address.postalCode,
        unitType: propertyAddress.address.unitType,
        unitNumber: propertyAddress.address.unitNumber
      }
      setCurrentAddressInfo(addressInfo);
    }
  }, []);

  useEffect(() => {
    if (!manualAddressShown) {
      methods.unregister([
        "streetNumber",
        "streetName",
        "unitNumber",
        "cityName",
        "provinceName",
        "postalCode",
        "country",
      ]);
      methods.register("autoAddress", {
        required: "Enter your address",
      });
    } else {
      {
        methods.unregister("autoAddress");
        methods.register("streetNumber", {
          required: "Enter your street number",
          pattern: {
            value: /^[ #a-zA-Z0-9._-]+$/,
            message: " Please enter a valid street number",
          },
        });
        methods.register("unitNumber", {
          required: methods.getValues("unitType")?.trim() != "" ? "Enter your unit number" : false,
          pattern: {
            value: /^[ #a-zA-Z0-9._-]+$/,
            message:
              " Please enter a valid unit number. Only letters, numbers and _-.# are accepted",
          },
        });
        methods.register("unitType", {
          required: methods.getValues("unitNumber")?.trim() != "" ? "Select a unit type" : false,
          pattern: {
            value: /^[ #a-zA-Z0-9._-]+$/,
            message: "Select a unit type",
          },
          validate: {
            unitType: (c) => {
              console.log(c);
              if (c.trim() != "" || methods.getValues("unitNumber")?.trim() == "") {
                return true
              } else {
                return "Select a unit type";
              }
            },
          },
        });
        methods.register("streetName", {
          required: "Enter your street name",
          pattern: {
            value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
            message: " Please enter a valid street name",
          },
        });
        methods.register("cityName", {
          required: "Enter your city name",
          pattern: {
            value: /^[ #a-zA-ZÀ-Ÿ0-9._'-]+$/,
            message: " Please enter a valid city name",
          },
        });
        methods.register("provinceName", {
          required: "Select your province name",
        });
        methods.register("postalCode", {
          required: "Enter your postal code",
        });
        methods.register("country", {
          required: "Select your country name",
          validate: {
            country: (v) => v == "Canada",
          },
        });
      }
    }
  }, [manualAddressShown, methods]);

  useEffect(() => {
    if (currentAddressInfo.streetNumber && currentAddressInfo.streetNumber.trim() != "" && !manualAddressShown) {
      setShowAddress(true);
      setAddressSelected(true);
      setAddressSelectionError(false);
    }

  }, [currentAddressInfo]);

  const formatAddress = () => {
    const span = document.getElementById("span-street-address");
    const span2 = document.getElementById("span-city");
    const span3 = document.getElementById("span-state");
    const span4 = document.getElementById("span-postcode");
    const span5 = document.getElementById("span-country");

    const streetAddress = span!.innerHTML.split(" ");
    const streetNumber = streetAddress.shift();

    methods.setValue("autoAddress", span!.innerHTML);
    methods.setValue("cityName", span2!.innerHTML);
    methods.setValue("provinceName", span3!.innerHTML);
    methods.setValue("postalCode", span4!.innerHTML);
    methods.setValue("country", span5!.innerHTML);
    methods.setValue("streetName", currentAddressInfo.streetName);

    if (streetNumber?.includes("-")) {
      const unit = streetNumber.split("-")[0];
      const number = streetNumber.split("-")[1];
      methods.setValue("unitNumber", unit!);
      methods.setValue("streetNumber", number!);
    } else {
      methods.setValue("unitNumber", "");
      methods.setValue("streetNumber", streetNumber!);
    }
  };

  const setPropertyAddress = () => {
    dispatch(setPAStreetNumber(methods.getValues("streetNumber")));
    dispatch(setPAStreetName(methods.getValues("streetName")));
    dispatch(setPAStreetType(methods.getValues("streetType")));
    dispatch(setPAStreetDirection(methods.getValues("streetDirection")));
    dispatch(setPAUnitNumber(methods.getValues("unitNumber")));
    dispatch(setPAUnitType(methods.getValues("unitType") != undefined ? methods.getValues("unitType").trim() : ""));
    dispatch(setPACity(methods.getValues("cityName")));
    dispatch(setPAProvince(methods.getValues("provinceName")));
    dispatch(setPAPostalCode(methods.getValues("postalCode")));
    dispatch(setPACountry(methods.getValues("country")));
  };

  const onError = (errors: any) => {
    setClickInsightFormErrors(errors);
    console.log(errors);
    if ((errors.unitType != undefined || errors.unitNumber != undefined) && !manualAddressShown) {
      toggleManualAddress();
    }
    window.scrollTo(0, 0);
  }

  const updateAppData = () => {
    client.apiApplicationsFetch().then((result) => {
      result.coapplicants = coapplicants;
      result.application = application;
      result.previousEmployment = previousEmployment;
      client.apiApplicationsSave(new ApplicationDto({
        applicationData: JSON.stringify(result)
      })).catch((error) => {
        console.log("Error saving application info");
      });
    }).catch((error) => {
      console.log("Error fetching application info");
    });
  }

  const onSubmit = async (data: any) => {
    if ((manualAddressShown && data.provinceName?.trim() != "ON") || displayError) {
      setDisplayError(true);
      return;
    }
    if (data.streetType.trim() == "") {
      window.scrollTo(0, 0);
      methods.setError("streetType", {
        type: "required",
        message: "Select your street type",
      });
      setmanualAddressShown(true);
      setShowAddress(false);
      return;
    }

    if (!addressSelected && !manualAddressShown) {
      setAddressSelectionError(true);
      return;
    }

    if (!manualAddressShown) {
      formatAddress();
    }

    setPropertyAddress();
    dispatch(setPurchasePrice(+price));
    dispatch(setPropertyValue(+propertyValueAmount));
    dispatch(setOriginalPurchasePrice(+originalPurchasePriceAmount));
    dispatch(setMortgageBalance(+remainingBalanceAmount));
    dispatch(setMortgageHolder(data.mortgageHolder));
    dispatch(setPropertyTax(+propertyTaxAmount));
    dispatch(setLivableArea(+data.livableArea));
    dispatch(setLivableAreaUnit(data.livableAreaUnit));
    dispatch(setHeatingCost(+heatingCostAmount));
    dispatch(setHeatingType(data.heatingType));
    dispatch(setMaintenanceFee(+maintenanceAmount));
    dispatch(setApplicationProgress(14));

    let selectedClosingDate = undefined;
    if (propertyInfo.closingDate != undefined && propertyInfo.closingDate != "") {
      let dateComponents = propertyInfo.closingDate.split("/");
      selectedClosingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
    }

    let selectedFundingDate = undefined;
    if (propertyInfo.fundingDate != undefined && propertyInfo.fundingDate != "") {
      let dateComponents = propertyInfo.fundingDate.split("/");
      selectedFundingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
    }

    //Update Property Info on Trellis Backend
    setIsSaving(true);
    await client.apiDealsApplicationsMortgagesSubjectProperty(new PropertyForCreationDto({
      applicationId: 0,
      remainingBalanceAmount: +remainingBalanceAmount,
      downPaymentAmount: application.downPayment,
      totalLoanAmount: application.applicationCategory == applicationCategory.Buy ? (+price - application.downPayment) : application.totalLoan,
      newMoneyRequestedAmount: 0,
      subjectProperty: new SubjectPropertyDto({
        originalPurchasePrice: +originalPurchasePriceAmount,
        purchasePrice: application.applicationCategory == applicationCategory.Buy ? +price : +propertyValueAmount,
        propertySalePrice: 0,
        address: new AddressDto({
          id: 0,
          streetNumber: methods.getValues("streetNumber"),
          streetName: methods.getValues("streetName"),
          streetType: methods.getValues("streetType"),
          streetDirection: methods.getValues("streetDirection"),
          unitType: methods.getValues("unitType"),
          unitNumber: methods.getValues("unitNumber"),
          city: methods.getValues("cityName"),
          region: methods.getValues("provinceName"),
          postalCode: methods.getValues("postalCode"),
          country: methods.getValues("country"),
          timeAtResidenceInMonths: 0,
          residenceStatusType_Name: "",
          deliveryMode: undefined,
          modeNumber: undefined,
          deliveryMode2: undefined,
          modeNumber2: undefined,
          isMailing: false
        }),
        propertyTax: +propertyTaxAmount,
        propertyTaxFrequency: "annually",
        mortgageHolder: data.mortgageHolder,
        livableArea: +data.livableArea,
        livableAreaType: data.livableAreaUnit,
        heatingCost: +heatingCostAmount,
        heatingType: data.heatingType,
        heatingCostFrequency: "monthly",
        condoFees: +maintenanceAmount,
        condoFeesFrequency: "monthly",
        futureStatusOfProperty: "",
        occupancyType: propertyInfo.purposeOfProperty != "" ? propertyInfo.purposeOfProperty : "Primary",
        subjectPropertyRentalUnits: propertyInfo.subjectPropertyRentalUnits,
        totalRentalUnits: 0,
        totalRentalProperties: 0,
        ownershipPercentage: propertyInfo.ownershipPercentage,
        hasCurrentRentalProperties: false,
        isPlanningOnRenting: propertyInfo.rentOut || propertyInfo.purposeOfProperty == "I'll rent it out",
        isSeparateLegalUnit: false,
        rent: propertyInfo.monthlyRent,
        estimatedFundingDate: selectedFundingDate,
        closingDate: selectedClosingDate,
        conditionOfFinancingDate: undefined,
        rentalIncome: propertyInfo.monthlyRent
      })
    })).then((result) => {

      console.log(result);
      setIsSaving(false);
      setClickInsightApplicationProgress('Your new home - Tell us about your property');
      navigate("/single/property-purpose");

    }).catch((error) => {
      setIsSaving(false);
      console.log("Error saving property information", error);
      navigate("/single/property-purpose");
    });
  };

  return (
    <>
      <SlideTransition>
        <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
          <div
            className={style.addHeading}
            role="region"
            aria-label="Address Header"
          >
            <h1 aria-label="Address Header">Tell us about your property</h1>
            <p className="sub-heading">Share the address, and some other details, about your property. If you have not finalized a property, you can add details of a property you have in mind.</p>
            {displayError &&
              <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                <img src="assets/error-info-icon.svg" alt="error" />
                <span className="text-sm ml-2 mt-1">
                  Cannot proceed, property must be located in Ontario.
                </span>
              </div>
            }
            <div className={"flex flex-row mt-5"}>
              <FormProvider {...methods}>
                <form
                  autoComplete="off"
                  onSubmit={methods.handleSubmit(onSubmit, onError)}
                >
                  <div className="col-span-1">
                    <div>
                      <div
                        className={clsx(
                          "flex mt-5",
                          style.Tin
                        )}
                      >
                        <span className="mr-1">Property Address</span>
                        <LegalToolTip
                          title=""
                          content="If you have not finalized a property, you can add the address of a property you have in mind."
                        />
                      </div>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/magnifying-glass-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.autoAddress
                                ? "hidden"
                                : "",
                              "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="search"
                            height={5}
                            width={5}
                          />
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.autoAddress
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          autoComplete="nope"
                          className={clsx(
                            methods.formState?.errors?.autoAddress?.message
                              ? "error"
                              : "",
                            "mt-2"
                          )}
                          id="search"
                          type="text"
                          placeholder="Start typing address"
                          {...methods.register("autoAddress")}
                        />
                      </div>
                      {addressSelectionError && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          You must select an address from the list of options
                        </span>
                      )}
                      <div
                        className={clsx("ml-2 mt-2", showAddress ? "" : "hidden")}
                      >
                        <p id="formatted-address"></p>
                        <span id="span-street-address"></span>
                        <br />
                        <span id="span-city"></span>&nbsp;
                        <span id="span-state"></span>&nbsp;
                        <span id="span-postcode"></span>
                        <br />
                        <span id="span-country"></span>
                      </div>
                      <div
                        className="flex relative mt-3"
                      >
                        <p className="active underline cursor-pointer" onClick={() => toggleManualAddress()}>{manualAddressShown ? "Hide address fields" : "Enter address manually"}</p>
                      </div>
                    </div>
                    <AnimatePresence>
                      {manualAddressShown &&
                        <motion.div
                          key={0}
                          initial={{ y: -100, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ opacity: 0 }}
                        ><ManualAddress addressData={currentAddressInfo} manualAddressToggled={manualAddressShown} forceOntario={true} /></motion.div>
                      }
                    </AnimatePresence>
                  </div>
                  {application.applicationCategory == applicationCategory.Buy &&
                    <div className="flex md:flex-row flex-col w-max mt-5">
                      <div>
                        <label htmlFor="title">Estimated Purchase Price</label>
                        <div className="flex relative">
                          <span>
                            <img
                              src="assets/error-icon.svg"
                              className={clsx(
                                methods.formState?.errors?.purchasePrice
                                  ? ""
                                  : "hidden",
                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                              )}
                              alt="error"
                              height={5}
                              width={5}
                            />
                          </span>
                          <CurrencyInput
                            id="purchasePrice"
                            placeholder="$100,000"
                            prefix={"$"}
                            decimalsLimit={2}
                            decimalScale={2}
                            allowNegativeValue={false}
                            maxLength={10}
                            value={price}
                            className={clsx(
                              methods.formState?.errors?.cityName?.message ? "error" : "",
                              "mt-2 w:[220px] md:w-[250px]"
                            )}
                            {...methods.register("purchasePrice", {
                              required: "Enter the estimated purchase price of your home",
                            })}
                            onValueChange={(value, name, values) => value != undefined ? setPrice(value) : setPrice("")}
                          />
                        </div>
                        {methods.formState?.errors?.purchasePrice?.message && (
                          <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                            {methods.formState.errors.purchasePrice.message}
                          </span>
                        )}
                      </div>
                    </div>
                  }
                  {(application.applicationCategory == applicationCategory.Refinance || application.applicationCategory == applicationCategory.Transfer) &&
                    <>
                      <div className="flex md:flex-row flex-col w-max mt-5">
                        <div>
                          <label htmlFor="title">Estimated property value</label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.propertyValue
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <CurrencyInput
                              id="propertyValue"
                              prefix={"$"}
                              decimalsLimit={2}
                              decimalScale={2}
                              allowNegativeValue={false}
                              value={propertyValueAmount}
                              maxLength={10}
                              className={clsx(
                                methods.formState?.errors?.propertyValue?.message ? "error" : "",
                                "mt-2 w:[220px] md:w-[250px]"
                              )}
                              {...methods.register("propertyValue", {
                                required: "Enter the estimated property value of your home",
                              })}
                              onValueChange={(value, name, values) => value != undefined ? setPropertyValueAmount(value) : setPropertyValueAmount("")}
                            />
                          </div>
                          {methods.formState?.errors?.propertyValue?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                              {methods.formState.errors.propertyValue.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex md:flex-row flex-col w-max mt-5">
                        <div>
                          <label htmlFor="title">Original purchase price</label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.originalPurchasePrice
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <CurrencyInput
                              id="originalPurchasePrice"
                              prefix={"$"}
                              decimalsLimit={2}
                              decimalScale={2}
                              allowNegativeValue={false}
                              value={originalPurchasePriceAmount}
                              maxLength={10}
                              className={clsx(
                                methods.formState?.errors?.originalPurchasePrice?.message ? "error" : "",
                                "mt-2 w:[220px] md:w-[250px]"
                              )}
                              {...methods.register("originalPurchasePrice", {
                                required: "Enter the original purchase price your home",
                              })}
                              onValueChange={(value, name, values) => value != undefined ? setOriginalPurchasePriceAmount(value) : setOriginalPurchasePriceAmount("")}
                            />
                          </div>
                          {methods.formState?.errors?.originalPurchasePrice?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                              {methods.formState.errors.originalPurchasePrice.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex md:flex-row flex-col w-max mt-5">
                        <div>
                          <label htmlFor="title">Current balance</label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.remainingBalance
                                    ? ""
                                    : "hidden",
                                  "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <CurrencyInput
                              id="remainingBalance"
                              prefix={"$"}
                              decimalsLimit={2}
                              decimalScale={2}
                              allowNegativeValue={false}
                              value={remainingBalanceAmount}
                              maxLength={10}
                              className={clsx(
                                methods.formState?.errors?.remainingBalance?.message ? "error" : "",
                                "mt-2 w:[220px] md:w-[250px]"
                              )}
                              {...methods.register("remainingBalance", {
                                required: "Enter the remaining balance of your existing mortgage",
                              })}
                              onValueChange={(value, name, values) => value != undefined ? setRemainingBalanceAmount(value) : setRemainingBalanceAmount("")}
                            />
                          </div>
                          {methods.formState?.errors?.remainingBalance?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                              {methods.formState.errors.remainingBalance.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex md:flex-row flex-col w-max mt-5">
                        <div>
                          <label htmlFor="lastName">Mortgage Lender</label>
                          <div className="flex relative">
                            <span>
                              <img
                                src="assets/error-icon.svg"
                                className={clsx(
                                  methods.formState?.errors?.mortgageHolder ? "" : "hidden",
                                  "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                )}
                                alt="error"
                                height={5}
                                width={5}
                              />
                            </span>
                            <input
                              className={clsx(
                                methods.formState?.errors?.mortgageHolder?.message
                                  ? "error"
                                  : "",
                                "mt-2"
                              )}
                              type="text"
                              id="mortgageHolder"
                              {...methods.register("mortgageHolder", {
                                required: "Enter the name of the mortgage lender",
                                maxLength: {
                                  value: 200,
                                  message: "Names exceeds maximum character count",
                                },
                                pattern: {
                                  value: /^[a-zA-Z0-9 '-.]*$/,
                                  message: "Please enter the current mortgage lender",
                                },
                                onBlur: () => {
                                  methods.setValue("mortgageHolder", methods.getValues("mortgageHolder").trim());
                                },
                              })}
                            />
                          </div>
                          <span className="flex items-center font-medium tracking-wide text-xs mt-1 ml-1">
                            Name of the Financial Institution the mortgage is currently with.
                          </span>
                          {methods.formState?.errors?.mortgageHolder?.message && (
                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                              {methods.formState.errors.mortgageHolder.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  }
                  <div className="flex md:flex-row flex-col w-max mt-5">
                    <div>
                      <label htmlFor="title">Estimated annual property tax</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.propertyTax
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <CurrencyInput
                          id="propertyTax"
                          placeholder="/per year"
                          prefix={"$"}
                          suffix="/year"
                          decimalsLimit={2}
                          decimalScale={2}
                          allowNegativeValue={false}
                          value={propertyTaxAmount}
                          maxLength={7}
                          className={clsx(
                            methods.formState?.errors?.propertyTax?.message ? "error" : "",
                            "mt-2 w:[220px] md:w-[250px]"
                          )}
                          {...methods.register("propertyTax", {
                            required: "Enter the property tax of your home",
                          })}
                          onValueChange={(value, name, values) => value != undefined ? setPropertyTaxAmount(value) : setPropertyTaxAmount("")}
                          transformRawValue={(value) => {
                            value = value.replace("/year", "");
                            if (value != undefined && value != "") {
                              methods.setValue("propertyTax", value + "/year");
                            }
                            return value
                          }}
                        />
                      </div>
                      {methods.formState?.errors?.propertyTax?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.propertyTax.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-row flex-col w-max mt-5">
                    <div>
                      <label htmlFor="phoneNumber">Estimated livable Area</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.livableArea
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[120px] xs:max-md:ml-[370px] md:ml-[345px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx(
                              methods.formState?.errors?.livableAreaUnit
                                ? ""
                                : "hidden",
                              "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[195px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <input
                          className={clsx(
                            "mt-2 w:[220px] md:w-[222px]", style.livableArea
                          )}
                          {...methods.register("livableArea", {
                            required: "Enter the livable area of your home.",
                            validate: (livableArea) => {
                              if (+livableArea > 0) {
                                return true;
                              } else {
                                return "Please enter a valid number.";
                              }
                            }
                          })}
                          type="number"
                          placeholder=""
                          id="livableArea"
                          onInput={(e: any) => methods.setValue("livableArea", e.target.value.slice(0, 10))}
                          onFocus={(e) => e.target.select()}
                        />
                        <select
                          id="livableAreaUnit"
                          {...methods.register("livableAreaUnit", {
                            required: "Select a unit",
                          })}
                          defaultValue=""
                          className={clsx(
                            style.frequency, "mt-2 pt-2.5 ml-3"
                          )}
                        >
                          <option hidden value="">
                            Choose...
                          </option>
                          <option value="squarefeet">Sq. Ft</option>
                          <option value="squaremetres">Sq. Metres</option>
                        </select>
                      </div>
                      {methods.formState?.errors?.livableArea?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.livableArea.message}
                        </span>
                      )}
                      {methods.formState?.errors?.livableAreaUnit?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.livableAreaUnit.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-row flex-col w-max mt-5">
                    <div>
                      <label htmlFor="title">Estimated Heating Cost</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx("hidden",
                              "w-4 h-4 absolute top-3.5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <CurrencyInput
                          id="heatingCost"
                          placeholder="/per month"
                          prefix={"$"}
                          suffix="/month"
                          decimalsLimit={2}
                          decimalScale={2}
                          allowNegativeValue={false}
                          maxLength={7}
                          value={heatingCostAmount}
                          className={clsx(
                            methods.formState?.errors?.heatingCost?.message ? "error" : "",
                            "mt-2 w:[220px] md:w-[250px]"
                          )}
                          {...methods.register("heatingCost", {
                            required: "Enter the estimated heating cost of your home",
                            validate: {
                              heatingCost: (c) => {
                                let currentAmount = c.replaceAll("$", "").replaceAll(",", "").replaceAll("/month", "");
                                if (+currentAmount > 0) {
                                  return true
                                } else {
                                  return "The heating cost cannot be zero."
                                }
                              },
                            },
                          })}
                          onValueChange={(value, name, values) => value != undefined ? setHeatingCostAmount(value) : setHeatingCostAmount("")}
                          transformRawValue={(value) => {
                            value = value.replace("/month", "");
                            if (value != undefined && value != "") {
                              methods.setValue("heatingCost", value + "/month");
                            }
                            return value
                          }}
                        />
                      </div>
                      {methods.formState?.errors?.heatingCost?.message && (
                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                          {methods.formState.errors.heatingCost.message}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-row flex-col w-max mt-5">
                    <div>
                      <label htmlFor="title">Heating Type (Optional)</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx("hidden",
                              "w-4 h-4 absolute top-3.5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <select
                          id="title"
                          {...methods.register("heatingType", {})}
                          defaultValue=""
                          className={clsx(
                            "", "mt-2 pt-2.5"
                          )}
                        >
                          <option hidden value="">
                            Choose...
                          </option>
                          <option value="electricbaseboard">Electric Baseboard</option>
                          <option value="forcedair">Forced Air Gas/Oil/Electric</option>
                          <option value="geothermal">Geothermal</option>
                          <option value="hotwater">Hot Water Heating</option>
                          <option value="oil">Oil</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="flex md:flex-row flex-col w-max mt-5">
                    <div>
                      <label htmlFor="title">Condo/Maintenance Fee (if applicable)</label>
                      <div className="flex relative">
                        <span>
                          <img
                            src="assets/error-icon.svg"
                            className={clsx("hidden",
                              "w-4 h-4 absolute top-3.5 max-xxs:ml-[40px] max-sm:ml-[70px] xs:max-md:ml-[80px] md:ml-[110px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                          />
                        </span>
                        <CurrencyInput
                          id="maintenanceFee"
                          placeholder="/per month"
                          prefix={"$"}
                          suffix="/month"
                          decimalsLimit={2}
                          decimalScale={2}
                          allowNegativeValue={false}
                          value={maintenanceAmount}
                          maxLength={7}
                          className={clsx(
                            methods.formState?.errors?.maintenanceFee?.message ? "error" : "",
                            "mt-2 w:[220px] md:w-[250px]"
                          )}
                          {...methods.register("maintenanceFee", {})}
                          onValueChange={(value, name, values) => value != undefined ? setMaintenanceAmount(value) : setMaintenanceAmount("")}
                          transformRawValue={(value) => {
                            value = value.replace("/month", "");
                            if (value != undefined && value != "") {
                              methods.setValue("maintenanceFee", value + "/month");
                            }
                            return value
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex max-sm:hidden">
                    <div className="mb-5 mr-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-169 sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                  </div>
                  <div className="sm:hidden">
                    <div className="mb-5">
                      <button
                        type="submit"
                        className="primary mt-5 w-full sm:w-28 h-11 px-6"
                        disabled={isSaving}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                        Continue
                      </button>
                    </div>
                    <div className="mb-5">
                      <button onClick={() => navigate(-1)}
                        type="button"
                        className="back mt-5 w-full sm:w-28 h-11 px-6">
                        Back
                      </button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </SlideTransition>
    </>
  );
}
